// src/components/InteriorDesignLandingPage.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Grow,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Divider,
  IconButton,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  alpha,
  Fade,
  Drawer,
  Menu,
  MenuItem,
  AppBar,
  Toolbar,
} from "@mui/material";
import {
  Phone,
  Email,
  LocationOn,
  CheckCircle,
  Facebook,
  Instagram,
  Twitter,
  ArrowForward,
  Carpenter,
  Home,
  Build,
  Map,
  Schedule,
  Toc,
  RemoveRedEye,
  Menu as MenuIcon,
  Close,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { resetFormStatus } from "../store";
import LeadForm from "./LeadForm";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import LogoImage from "../images/decodia-logo.png";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CallIcon from "@mui/icons-material/Call"; // Styled Components
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

const HeroSection = styled(Box)(({ theme }) => ({
  position: "relative",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  color: "white",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.2)),
      url('https://images.unsplash.com/photo-1615873968403-89e068629265?auto=format&fit=crop&w=1920&q=80')
    `,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundAttachment: "fixed",
    transform: "scale(1.1)",
    [theme.breakpoints.down("lg")]: {
      backgroundAttachment: "scroll",
      filter: "blur(2px)", // Less blur on tablet
    },
    [theme.breakpoints.down("md")]: {
      filter: "blur(1px)", // Even less blur on mobile
    },
    [theme.breakpoints.down("sm")]: {
      filter: "blur(1px)", // Minimal blur on small mobile
    },
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.2)",
  },

  "& > *": {
    position: "relative",
    zIndex: 2,
  },

  [theme.breakpoints.down("md")]: {
    minHeight: "auto",
    padding: theme.spacing(12, 0, 6),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(8, 0, 4),
  },
  "@media (max-width: 480px)": {
    padding: theme.spacing(6, 0, 3),
  },
}));

const ServiceCard = styled(Card)(({ theme }) => ({
  height: "100%",
  border: "1px solid #e0e0e0",
  [theme.breakpoints.down("sm")]: {
    margin: "0 auto",
    maxWidth: "90%",
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  borderRadius: 2,
  fontWeight: 600,
  textTransform: "none",
  fontSize: "1rem",
  backgroundColor: "#8B4513",
  "&:hover": {
    backgroundColor: "#654321",
  },
  [theme.breakpoints.down("lg")]: {
    padding: theme.spacing(1.2, 3),
  },
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(1, 2.5),
    fontSize: "0.9rem",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.8, 2),
    fontSize: "0.85rem",
  },
  "@media (max-width: 480px)": {
    padding: theme.spacing(0.6, 1.5),
    fontSize: "0.8rem",
  },
}));

const OutlineButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  borderRadius: 2,
  fontWeight: 600,
  textTransform: "none",
  fontSize: "1rem",
  border: "2px solid white",
  color: "white",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    border: "2px solid white",
  },
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(1, 2.5),
    fontSize: "0.9rem",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.8, 2),
    fontSize: "0.85rem",
  },
  "@media (max-width: 480px)": {
    padding: theme.spacing(0.6, 1.5),
    fontSize: "0.8rem",
  },
}));

const PreviewCard = styled(Card)(({ theme }) => ({
  border: "1px solid #e0e0e0",
  height: "100%",
}));

const classicContactInfo = [
  {
    icon: <HomeWorkOutlinedIcon fontSize="large" />,
    title: "Home Design",
    details: [
      "We create elegant, functional homes that reflect your lifestyle. Every detail is thoughtfully planned — from layout and lighting to materials and finishes.",
    ],
    color: "#ffffff",
    bgColor: "#a97100ff",
    delay: 100,
  },
  {
    icon: <BusinessCenterOutlinedIcon fontSize="large" />,
    title: "Office Design",
    details: [
      "Our office designs balance aesthetics with productivity, creating inspiring workspaces that enhance workflow and reflect your brand identity.",
    ],
    color: "#a97100ff",
    bgColor: "#ffffff",
    delay: 200,
  },
  {
    icon: <HotelOutlinedIcon fontSize="large" />,
    title: "Hotel Design",
    details: [
      "We design luxury hospitality spaces that elevate guest experience while maintaining operational efficiency and timeless elegance.",
    ],
    color: "#ffffff",
    bgColor: "#a97100ff",
    delay: 300,
  },
];

const InteriorDesignLandingPage = () => {
  const theme = useTheme();
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up("xl"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isXSmallMobile = useMediaQuery("(max-width: 480px)");

  const dispatch = useDispatch();
  const { submitSuccess, submitError } = useSelector((state) => state.leadForm);
  const [tabValue, setTabValue] = React.useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headerBg, setHeaderBg] = useState("transparent");

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setHeaderBg("rgba(44, 24, 16, 0.95)");
      } else {
        setHeaderBg("transparent");
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCloseSnackbar = () => {
    dispatch(resetFormStatus());
  };

  const scrollToForm = () => {
    document.getElementById("contact-section").scrollIntoView({
      behavior: "smooth",
    });
    setMobileMenuOpen(false);
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId).scrollIntoView({
      behavior: "smooth",
    });
    setMobileMenuOpen(false);
  };

  // Navigation items
  const navItems = [
    { id: "services-section", label: "Services" },
    { id: "projects-section", label: "Projects" },
    { id: "contact-section", label: "Contact" },
  ];

  // Table of Contents items
  const tocItems = [
    { id: "services-section", label: "Our Services", icon: <Carpenter /> },
    { id: "projects-section", label: "Our Projects", icon: <Home /> },
    { id: "contact-section", label: "Contact Us", icon: <Phone /> },
  ];

  const previewContent = [
    {
      title: "Luxury Furniture Design",
      description:
        "Bespoke furniture pieces crafted with precision, combining aesthetics with functionality for your unique space.",
      image:
        "https://images.unsplash.com/photo-1613545325278-f24b0cae1224?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80",
    },
    {
      title: "Interior Design",
      description:
        "Complete interior design solutions tailored to your style and functional needs.",
      image:
        "https://images.unsplash.com/photo-1616594039964-ae9021a400a0?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80",
    },
    {
      title: "Project Gallery",
      description:
        "Browse our portfolio of completed projects showcasing our attention to detail and quality.",
      image: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3",
    },
  ];
  const services = [
    {
      icon: <Carpenter />,
      title: "Residential Design",
      description:
        "We design warm, elegant living spaces tailored to your lifestyle. From custom furniture to fine finishes, every element is thoughtfully crafted to create a home that feels personal, comfortable, and timeless.",
      details: [
        "Furniture Design & Build",
        "Custom Cabinetry",
        "Wood Flooring",
        "Wood Paneling",
        "Custom Shelving",
      ],
    },
    {
      icon: <Home />,
      title: "Commercial Design",
      description:
        "Our commercial interiors are designed to reflect your brand while enhancing functionality. We create smart, visually engaging spaces that support productivity, customer experience, and long-term growth.",
      details: [
        "Space Planning",
        "Interior Design",
        "Material Selection",
        "Color Consulting",
        "Lighting Design",
      ],
    },
    {
      icon: <Build />,
      title: "Renovation Projects",
      description:
        "We transform existing spaces with precision and care. From concept to completion, our renovation solutions focus on improving aesthetics, functionality, and quality—without compromising on timelines.",
      details: [
        "Complete Interior Design",
        "Custom Millwork",
        "Installation Services",
        "Project Management",
        "Final Touches",
      ],
    },
  ];

 const projects = [
    {
      title: "Custom Home Interiors",
      image:
        "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      description:
        "Custom Home Interiors services for your home. We offer a wide range of services, including furniture design, cabinetry, and flooring.",
    },
    {
      title: "Custom Office Interiors",
      image:
        "https://images.unsplash.com/photo-1497366754035-f200968a6e72?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      description:
        "Custom office interiors are designed to meet the specific needs of your home.",
    },
    {
      title: "New Home Interiors",
      image:
        "https://images.unsplash.com/photo-1600210492493-0946911123ea?w=800&auto=format&fit=crop",
      description:
        "New home interiors are designed to meet the specific needs of your new home.",
    },
  ];

  return (
    <Box sx={{ overflowX: "hidden" }}>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: headerBg,
          backdropFilter: headerBg !== "transparent" ? "blur(10px)" : "none",
          transition: "all 0.3s ease",
          boxShadow: headerBg !== "transparent" ? 3 : 0,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            sx={{
              justifyContent: "space-between",
              py: { xs: 1, sm: 1.5, md: 2 },
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, sm: 1.5, md: 2 },
              }}
            >
              <img
                src={LogoImage}
                alt="DECODIA"
                style={{
                  height: isXSmallMobile
                    ? 35
                    : isSmallMobile
                      ? 40
                      : isMobile
                        ? 45
                        : 60,
                }}
              />
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{
                  fontWeight: 700,
                  color: "#D2691E",
                  fontSize: {
                    xs: "0.9rem",
                    sm: "1.1rem",
                    md: "1.5rem",
                    lg: "1.75rem",
                  },
                }}
              >
                DECODIA{" "}
                <Box
                  component="span"
                  sx={{
                    color: "#e8dcd3ff",
                    fontSize: {
                      xs: "0.75rem",
                      sm: "0.9rem",
                      md: "1.1rem",
                      lg: "1.3rem",
                    },
                  }}
                >
                  {isSmallMobile ? "DESIGN" : "DESIGN STUDIO"}
                </Box>
              </Typography>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  {/* {navItems.map((item) => (
                    <Button
                      key={item.id}
                      color="inherit"
                      onClick={() => scrollToSection(item.id)}
                      sx={{
                        fontSize: {
                          md: "0.875rem",
                          lg: "0.95rem",
                          xl: "1rem",
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  ))} */}
                </Box>
                <StyledButton
                  variant="contained"
                  onClick={scrollToForm}
                  startIcon={<MailOutlineIcon />}
                  sx={{
                    ml: 2,
                    fontSize: {
                      md: "0.85rem",
                      lg: "0.9rem",
                      xl: "1rem",
                    },
                  }}
                >
                  {isDesktop
                    ? "info@decodia.in"
                    : "info@decodia.in"}
                </StyledButton>
                <StyledButton
                  variant="contained"
                  onClick={scrollToForm}
                  startIcon={<CallIcon />}
                  sx={{
                    ml: 2,
                    fontSize: {
                      md: "0.85rem",
                      lg: "0.9rem",
                      xl: "1rem",
                    },
                  }}
                >
                  {isDesktop ? "+91 83708 25457" : "Call Now"}
                </StyledButton>
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <StyledButton
                  variant="contained"
                  onClick={scrollToForm}
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    fontSize: "0.8rem",
                    padding: "4px 12px",
                  }}
                >
                  Call Now
                </StyledButton>
                <IconButton
                  color="inherit"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  sx={{ ml: 1 }}
                >
                  {mobileMenuOpen ? <Close /> : <MenuIcon />}
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: {
            xs: "#2C1810", // Always this color on mobile (xs breakpoint)
            md: headerBg, // Original behavior on desktop
          },
          backdropFilter: {
            xs: "none", // No blur on mobile since we have solid color
            md: headerBg !== "transparent" ? "blur(10px)" : "none",
          },
          transition: "all 0.3s ease",
          boxShadow: {
            xs: 3, // Always have shadow on mobile
            md: headerBg !== "transparent" ? 3 : 0,
          },
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            sx={{
              justifyContent: "space-between",
              py: { xs: 1, sm: 1.5, md: 2 },
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, sm: 1.5, md: 2 },
              }}
            >
              <img
                src={LogoImage}
                alt="DECODIA"
                style={{
                  height: isXSmallMobile
                    ? 35
                    : isSmallMobile
                      ? 40
                      : isMobile
                        ? 45
                        : 60,
                }}
              />
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{
                  fontWeight: 700,
                  color: "#D2691E",
                  fontSize: {
                    xs: "0.9rem",
                    sm: "1.1rem",
                    md: "1.5rem",
                    lg: "1.75rem",
                  },
                }}
              >
                DECODIA{" "}
                <Box
                  component="span"
                  sx={{
                    color: "#e8dcd3ff",
                    fontSize: {
                      xs: "0.75rem",
                      sm: "0.9rem",
                      md: "1.1rem",
                      lg: "1.3rem",
                    },
                  }}
                >
                  {isSmallMobile ? "DESIGN" : "DESIGN STUDIO"}
                </Box>
              </Typography>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  {/* {navItems.map((item) => (
              <Button
                key={item.id}
                color="inherit"
                onClick={() => scrollToSection(item.id)}
                sx={{
                  fontSize: {
                    md: "0.875rem",
                    lg: "0.95rem",
                    xl: "1rem",
                  },
                }}
              >
                {item.label}
              </Button>
            ))} */}
                </Box>
                <StyledButton
                  variant="contained"
                  onClick={scrollToForm}
                  startIcon={<MailOutlineIcon />}
                  sx={{
                    ml: 2,
                    fontSize: {
                      md: "0.85rem",
                      lg: "0.9rem",
                      xl: "1rem",
                    },
                  }}
                >
                  {isDesktop
                    ? "info@decodia.in"
                    : "info@decodia.in"}
                </StyledButton>
                <StyledButton
                  variant="contained"
                  onClick={scrollToForm}
                  startIcon={<CallIcon />}
                  sx={{
                    ml: 2,
                    fontSize: {
                      md: "0.85rem",
                      lg: "0.9rem",
                      xl: "1rem",
                    },
                  }}
                >
                  {isDesktop ? "+91 83708 25457" : "Call Now"}
                </StyledButton>
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <StyledButton
                  variant="contained"
                  onClick={scrollToForm}
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    fontSize: "0.8rem",
                    padding: "4px 12px",
                  }}
                >
                  Call Now
                </StyledButton>
                <IconButton
                  color="inherit"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  sx={{ ml: 1 }}
                >
                  {mobileMenuOpen ? <Close /> : <MenuIcon />}
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: isXSmallMobile ? "85%" : "300px",
            backgroundColor: "#2C1810",
            color: "white",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Typography variant="h6" sx={{ color: "#D2691E", fontWeight: 700 }}>
              Menu
            </Typography>
            <IconButton
              color="inherit"
              onClick={() => setMobileMenuOpen(false)}
            >
              <Close />
            </IconButton>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {navItems.map((item) => (
              <Button
                key={item.id}
                fullWidth
                color="inherit"
                onClick={() => scrollToSection(item.id)}
                sx={{
                  justifyContent: "flex-start",
                  py: 1.5,
                  fontSize: "1rem",
                }}
              >
                {item.label}
              </Button>
            ))}
            <Divider sx={{ my: 2, bgcolor: "rgba(255,255,255,0.1)" }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Button
                startIcon={<Email />}
                color="inherit"
                sx={{ justifyContent: "flex-start" }}
                onClick={() => {
                  window.location.href = "mailto:info@decodia.in";
                  setMobileMenuOpen(false);
                }}
              >
                info@decodia.in
              </Button>
              <Button
                startIcon={<Phone />}
                color="inherit"
                sx={{ justifyContent: "flex-start" }}
                onClick={() => {
                  window.location.href = "tel:+918370825457";
                  setMobileMenuOpen(false);
                }}
              >
                +91 83708 25457
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>
      {/* Hero Section */}
      <HeroSection>
        <Container
          sx={{
            position: "relative",
            zIndex: 2,
            mt: { xs: 8, sm: 9, md: 10 },
          }}
        >
          <Grid container spacing={4} alignItems="center">
            {/* Left Side: Text Content */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  pl: { xs: 0, md: 4, lg: 6, xl: 8 },
                  pr: { xs: 0, md: 2 },
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                {/* Main Heading */}
                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: 600,
                    mb: { xs: 2, sm: 3 },
                    fontSize: {
                      xs: "1rem",
                      sm: "1.5rem",
                      md: "1.5rem",
                      lg: "2rem",
                      xl: "3rem",
                    },
                    lineHeight: 1.1,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      color: { xs: "rgba(220, 216, 213, 1)", md: "white" },
                      display: "block",
                    }}
                  >
                    Designs that
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      color: "rgb(183, 116, 70)",
                      display: "block",
                    }}
                  >
                    speak luxury ,
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      color: "rgb(183, 116, 70)",
                      display: "block",
                    }}
                  >
                    rooted in concept
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      color: { xs: "rgba(220, 216, 213, 1)", md: "white" },
                      display: "block",
                    }}
                  >
                    and crafted with purpose.
                  </Box>
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    mb: { xs: 3, sm: 4, md: 5 },
                    opacity: 0.9,
                    fontSize: {
                      xs: "0.8rem", // Default mobile
                      sm: "1rem", // Small tablet
                      md: "1.3rem", // Desktop
                      lg: "1.3rem", // Large desktop
                    },
                    // Additional media query for very small phones
                    "@media (max-width: 380px)": {
                      fontSize: "0.7rem",
                    },
                    lineHeight: 1.6,
                    maxWidth: "600px",
                    fontWeight: 300,
                    fontStyle: "italic",
                    mx: { xs: "auto", md: 0 },
                    position: "relative",
                    pl: { xs: 2, md: 3 },
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: { xs: "2px", md: "3px" },
                      background: "#D2691E",
                      borderRadius: "2px",
                    },
                  }}
                >
                  Spaces designed to reflect your lifestyle — quietly elegant,
                  thoughtfully planned.
                </Typography>

                {/* CTA Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    gap: { xs: 1, sm: 3 }, // Smaller gap on mobile
                    flexDirection: { xs: "row", sm: "row" }, // Keep them in a row on mobile
                    flexWrap: "wrap",
                    justifyContent: { xs: "center", md: "flex-start" },
                    mb: { xs: 4, md: 5 },
                  }}
                >
                  <StyledButton
                    variant="contained"
                    size="large"
                    onClick={scrollToForm}
                    startIcon={<Carpenter />}
                    sx={{
                      minWidth: { xs: "120px", sm: "auto" }, // Fixed minimum width on mobile
                      flex: { xs: "0 1 auto", sm: "0 1 auto" },
                      padding: {
                        xs: "4px 8px", // Even smaller padding on mobile
                        sm: "8px 22px",
                        md: "12px 32px",
                      },
                      fontSize: {
                        xs: "0.65rem", // Smaller font
                        sm: "0.85rem",
                        md: "1rem",
                      },
                      "& .MuiButton-startIcon": {
                        marginRight: { xs: "2px", sm: "8px" },
                        "& > *:first-of-type": {
                          fontSize: { xs: "0.9rem", sm: "1.25rem" },
                        },
                      },
                    }}
                  >
                    Start
                  </StyledButton>

                  <OutlineButton
                    variant="outlined"
                    size="large"
                    onClick={() => scrollToSection("preview-section")}
                    startIcon={<RemoveRedEye />}
                    sx={{
                      minWidth: { xs: "100px", sm: "auto" }, // Fixed minimum width on mobile
                      flex: { xs: "0 1 auto", sm: "0 1 auto" },
                      padding: {
                        xs: "4px 8px", // Even smaller padding on mobile
                        sm: "8px 22px",
                        md: "12px 32px",
                      },
                      fontSize: {
                        xs: "0.65rem", // Smaller font
                        sm: "0.85rem",
                        md: "1rem",
                      },
                      borderWidth: {
                        xs: "1px",
                        sm: "2px",
                      },
                      "& .MuiButton-startIcon": {
                        marginRight: { xs: "2px", sm: "8px" },
                        "& > *:first-of-type": {
                          fontSize: { xs: "0.9rem", sm: "1.25rem" },
                        },
                      },
                    }}
                  >
                    Preview
                  </OutlineButton>
                </Box>

                {/* Stats */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: { xs: 2, sm: 3, md: 4 },
                    justifyContent: { xs: "center", md: "flex-start" },
                  }}
                >
                  {["8+ Years Experience"].map((text, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <CheckCircle
                        sx={{
                          color: "#D2691E",
                          fontSize: {
                            xs: "1rem",
                            sm: "1.1rem",
                            md: "1.2rem",
                          },
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          opacity: 0.8,
                          fontSize: {
                            xs: "0.75rem",
                            sm: "0.85rem",
                            md: "0.9rem",
                          },
                        }}
                      >
                        {text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Right Side: Form (Desktop only) */}
            {!isMobile && (
              <Grid item xs={12} md={6}>
                <Fade in timeout={1000}>
                  <Paper
                    elevation={24}
                    sx={{
                      bgcolor: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(10px)",
                      p: { md: 3, lg: 4 },
                      borderRadius: "20px",
                      border: "1px solid rgba(139, 69, 19, 0.2)",
                      transform: "translateY(0)",
                      transition: "transform 0.3s ease",
                      maxWidth: "500px",
                      mx: "auto",
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        mb: 3,
                        color: "#8B4513",
                        fontWeight: 700,
                        textAlign: "center",
                        fontSize: {
                          md: "1rem",
                          lg: "1.5rem",
                        },
                      }}
                    >
                      Get Your Free Consultation
                    </Typography>
                    <LeadForm />
                  </Paper>
                </Fade>
              </Grid>
            )}
          </Grid>
        </Container>
      </HeroSection>

      {/* Mobile Form (if not in hero) */}
      {isMobile && (
        <Container sx={{ mt: 4, mb: 6 }}>
          <Paper
            elevation={8}
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.95)",
              p: 3,
              borderRadius: "15px",
              border: "1px solid rgba(139, 69, 19, 0.2)",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                color: "#8B4513",
                fontWeight: 700,
                textAlign: "center",
                fontSize: { xs: "1.1rem", sm: "1.3rem" },
              }}
            >
              Get Your Free Consultation
            </Typography>
            <LeadForm />
          </Paper>
        </Container>
      )}

      {/* Contact Info Cards */}
      <Container sx={{ py: { xs: 4, sm: 6, md: 8 } }}>
        <Grid container spacing={3}>
          {classicContactInfo.map((info, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Grow in timeout={info.delay}>
                <Card
                  elevation={4}
                  onClick={info.action}
                  sx={{
                    height: "100%",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    borderRadius: "12px",
                    overflow: "hidden",
                    bgcolor: index % 2 === 0 ? "#8B4513" : "#ffffff",
                    color: index % 2 === 0 ? "white" : "#8B4513",
                  }}
                >
                  <CardContent
                    sx={{ p: { xs: 2, sm: 3 }, textAlign: "center" }}
                  >
                    <Box
                      sx={{
                        width: { xs: 50, sm: 60, md: 70 },
                        height: { xs: 50, sm: 60, md: 70 },
                        borderRadius: "50%",
                        bgcolor: index % 2 === 0 ? "white" : "#8B4513",
                        color: index % 2 === 0 ? "#8B4513" : "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 16px",
                        fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                      }}
                    >
                      {info.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 700,
                        fontSize: {
                          xs: "0.9rem",
                          sm: "1rem",
                          md: "1.1rem",
                        },
                      }}
                    >
                      {info.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: {
                          xs: "0.75rem",
                          sm: "0.8rem",
                          md: "0.875rem",
                        },
                      }}
                    >
                      {info.details[0]}
                    </Typography>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Table of Contents / Preview Section */}
      <Box
        id="preview-section"
        sx={{
          py: { xs: 4, sm: 6, md: 8 },
          bgcolor: "#FAF3EB",
        }}
      >
        <Container>
          <Box sx={{ textAlign: "center", mb: { xs: 4, sm: 6 } }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: "#8B4513",
                fontSize: {
                  xs: "1.5rem",
                  sm: "2rem",
                  md: "2.5rem",
                  lg: "3rem",
                },
                mb: 2,
              }}
            >
              TABLE OF CONTENTS
            </Typography>
            <Divider
              sx={{
                width: { xs: 80, sm: 100, md: 120 },
                height: 3,
                bgcolor: "#D2691E",
                mx: "auto",
              }}
            />
          </Box>

          {/* Tabs */}
          <Box sx={{ mb: { xs: 4, sm: 6 } }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              centered
              variant={isXSmallMobile ? "fullWidth" : "standard"}
              sx={{
                "& .MuiTab-root": {
                  fontSize: {
                    xs: "0.75rem",
                    sm: "0.85rem",
                    md: "0.95rem",
                  },
                  minHeight: { xs: 48, sm: 56 },
                  minWidth: { xs: "auto", sm: 120 },
                },
              }}
            >
              <Tab
                icon={<RemoveRedEye />}
                iconPosition="start"
                label={isXSmallMobile ? "Preview" : "Content Preview"}
              />
              <Tab
                icon={<Toc />}
                iconPosition="start"
                label={isXSmallMobile ? "TOC" : "Table of Contents"}
              />
            </Tabs>
          </Box>

          {/* Tab Content */}
          {tabValue === 0 ? (
            <Grid container spacing={3}>
              {previewContent.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <PreviewCard>
                    <CardMedia
                      component="img"
                      height={isXSmallMobile ? 150 : isSmallMobile ? 180 : 200}
                      image={item.image}
                      alt={item.title}
                      sx={{ objectFit: "cover" }}
                    />
                    <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                          color: "#8B4513",
                          fontSize: {
                            xs: "0.9rem",
                            sm: "1rem",
                            md: "1.1rem",
                          },
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          fontSize: {
                            xs: "0.75rem",
                            sm: "0.8rem",
                            md: "0.875rem",
                          },
                        }}
                      >
                        {item.description}
                      </Typography>
                      {/* <Button
                        variant="outlined"
                        fullWidth
                        size="small"
                        sx={{
                          borderColor: "#8B4513",
                          color: "#8B4513",
                          fontSize: {
                            xs: "0.7rem",
                            sm: "0.75rem",
                            md: "0.8rem",
                          },
                        }}
                      >
                        Explore More
                      </Button> */}
                    </CardContent>
                  </PreviewCard>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={2}>
              {tocItems.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                      bgcolor: "white",
                      cursor: "pointer",
                    }}
                    onClick={() => scrollToSection(item.id)}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          width: { xs: 36, sm: 40 },
                          height: { xs: 36, sm: 40 },
                          borderRadius: "50%",
                          bgcolor: "#FAF3EB",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#8B4513",
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: "#8B4513",
                          fontSize: {
                            xs: "0.8rem",
                            sm: "0.9rem",
                            md: "1rem",
                          },
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>

      {/* Services Section */}
      <Box id="services-section" sx={{ py: { xs: 4, sm: 6, md: 8 } }}>
        <Container>
          <Box sx={{ textAlign: "center", mb: { xs: 4, sm: 6 } }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: "#8B4513",
                fontSize: {
                  xs: "1.5rem",
                  sm: "2rem",
                  md: "2.5rem",
                  lg: "3rem",
                },
                mb: 2,
              }}
            >
              Our Services
            </Typography>
            <Divider
              sx={{
                width: { xs: 60, sm: 80, md: 100 },
                height: 3,
                bgcolor: "#D2691E",
                mx: "auto",
              }}
            />
          </Box>

          <Grid container spacing={3}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <ServiceCard>
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box
                      sx={{
                        color: "#8B4513",
                        fontSize: { xs: 36, sm: 40, md: 44 },
                        mb: 2,
                        textAlign: "center",
                      }}
                    >
                      {service.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 1,
                        fontWeight: 600,
                        textAlign: "center",
                        fontSize: {
                          xs: "0.9rem",
                          sm: "1rem",
                          md: "1.1rem",
                        },
                      }}
                    >
                      {service.title}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        fontSize: {
                          xs: "0.75rem",
                          sm: "0.8rem",
                          md: "0.875rem",
                        },
                        textAlign: "center",
                      }}
                    >
                      {service.description}
                    </Typography>
                    <List dense sx={{ mb: 1 }}>
                      {service.details.map((detail, idx) => (
                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckCircle
                              sx={{
                                color: "#8B4513",
                                fontSize: { xs: 16, sm: 18 },
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={detail}
                            primaryTypographyProps={{
                              sx: {
                                fontSize: {
                                  xs: "0.7rem",
                                  sm: "0.75rem",
                                  md: "0.8rem",
                                },
                              },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </ServiceCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Projects Section */}
      <Box
        id="projects-section"
        sx={{
          bgcolor: "#FAF3EB",
          py: { xs: 4, sm: 6, md: 8 },
        }}
      >
        <Container>
          <Box sx={{ textAlign: "center", mb: { xs: 4, sm: 6 } }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: "#8B4513",
                fontSize: {
                  xs: "1.5rem",
                  sm: "2rem",
                  md: "2.5rem",
                  lg: "3rem",
                },
                mb: 2,
              }}
            >
              Our Projects
            </Typography>
            <Divider
              sx={{
                width: { xs: 60, sm: 80, md: 100 },
                height: 3,
                bgcolor: "#D2691E",
                mx: "auto",
              }}
            />
          </Box>

          <Grid container spacing={3}>
            {projects.map((project, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: "100%", // Makes all cards take full height of grid item
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardMedia
                    component="img"
                    height={isXSmallMobile ? 150 : isSmallMobile ? 180 : 200}
                    image={project.image}
                    alt={project.title}
                    sx={{ objectFit: "cover" }}
                  />
                  <CardContent
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      flexGrow: 1, // Allows content to expand
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 1,
                        fontWeight: 600,
                        color: "#8B4513",
                        fontSize: {
                          xs: "0.9rem",
                          sm: "1rem",
                          md: "1.1rem",
                        },
                        // Ensure title takes consistent height (2 lines max)
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        minHeight: "2.8em", // Approx height for 2 lines
                      }}
                    >
                      {project.title}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{
                        fontSize: {
                          xs: "0.75rem",
                          sm: "0.8rem",
                          md: "0.875rem",
                        },
                        // Ensure description takes consistent height (3 lines max)
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        minHeight: "3.6em", // Approx height for 3 lines
                      }}
                    >
                      {project.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box id="contact-section" sx={{ py: { xs: 4, sm: 6, md: 8 } }}>
        <Container>
          <Box sx={{ textAlign: "center", mb: { xs: 4, sm: 6 } }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: "#8B4513",
                fontSize: {
                  xs: "1.5rem",
                  sm: "2rem",
                  md: "2.5rem",
                  lg: "3rem",
                },
                mb: 2,
              }}
            >
              Contact Us
            </Typography>
            <Divider
              sx={{
                width: { xs: 60, sm: 80, md: 100 },
                height: 3,
                bgcolor: "#D2691E",
                mx: "auto",
              }}
            />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  bgcolor: "#FAF3EB",
                  height: "100%",
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: { xs: 2, sm: 3 },
                    fontWeight: 600,
                    color: "#8B4513",
                    fontSize: {
                      xs: "0.9rem",
                      sm: "1rem",
                      md: "1.1rem",
                    },
                  }}
                >
                  Contact Information
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    <Email
                      sx={{ color: "#8B4513", mt: 0.5, fontSize: "1.2rem" }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Email
                      </Typography>
                      <Typography variant="body2">
                        info@decodia.in
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    <Phone
                      sx={{ color: "#8B4513", mt: 0.5, fontSize: "1.2rem" }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Phone
                      </Typography>
                      <Typography variant="body2">+91 83708 25457</Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    <LocationOn
                      sx={{ color: "#8B4513", mt: 0.5, fontSize: "1.2rem" }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Location
                      </Typography>
                      <Typography variant="body2">
                        Decodia P S Abacus - 703, 7th Floor <br />
                        NH12, Action Area IIE, Newtown,
                        <br />
                        Kolkata , West Bengal 700161
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: 600, color: "#8B4513" }}
                >
                  Follow Us
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    component="a"
                    href="https://www.facebook.com/decodiadesignstudio"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      backgroundColor: "rgba(129, 126, 126, 0.3)",
                      color: "#1e87d7ff",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: `0 6px 20px #E4405F40`,
                      },
                    }}
                  >
                    <FacebookIcon />
                  </IconButton>

                  <IconButton
                    component="a"
                    href="https://www.instagram.com/decodiadesignstudio/?igsh=MXVrMmgzMHdxeG1wcg%3D%3D#"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      backgroundColor: "rgba(129, 126, 126, 0.3)",
                      color: "#a53f3fff",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: `0 6px 20px #E4405F40`,
                      },
                    }}
                  >
                    <InstagramIcon />
                  </IconButton>
                  <IconButton
                    component="a"
                    href="https://www.linkedin.com/company/decodiadesignsstudio/posts/?feedView=all"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      backgroundColor: "rgba(129, 126, 126, 0.3)",
                      color: "#1c2b80ff",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: `0 6px 20px #E4405F40`,
                      },
                    }}
                  >
                    <LinkedInIcon />
                  </IconButton>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={3}
                sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    fontWeight: 600,
                    color: "#8B4513",
                    fontSize: {
                      xs: "0.9rem",
                      sm: "1rem",
                      md: "1.1rem",
                    },
                  }}
                >
                  Get Your Free Consultation
                </Typography>
                <LeadForm />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{ bgcolor: "#2C1810", color: "white", py: { xs: 3, sm: 4, md: 5 } }}
      >
        <Container>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  color: "#D2691E",
                  fontSize: {
                    xs: "0.9rem",
                    sm: "1rem",
                    md: "1.1rem",
                  },
                }}
              >
                DECODIA DESIGN STUDIO
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: {
                    xs: "0.7rem",
                    sm: "0.75rem",
                    md: "0.8rem",
                  },
                }}
              >
                Expert hands creating timeless woodwork and beautiful interiors
                since 2023.
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: "#D2691E",
                  fontSize: {
                    xs: "0.8rem",
                    sm: "0.85rem",
                    md: "0.9rem",
                  },
                }}
              ></Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {/* {navItems.map((item) => (
                  <Button
                    key={item.id}
                    color="inherit"
                    size="small"
                    onClick={() => scrollToSection(item.id)}
                    sx={{
                      justifyContent: "flex-start",
                      fontSize: {
                        xs: "0.7rem",
                        sm: "0.75rem",
                        md: "0.8rem",
                      },
                      px: 0,
                    }}
                  >
                    {item.label}
                  </Button>
                ))} */}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: "#D2691E",
                  fontSize: {
                    xs: "0.8rem",
                    sm: "0.85rem",
                    md: "0.9rem",
                  },
                }}
              >
                Contact
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: {
                    xs: "0.7rem",
                    sm: "0.75rem",
                    md: "0.8rem",
                  },
                }}
              >
                Email: info@decodia.in
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: {
                    xs: "0.7rem",
                    sm: "0.75rem",
                    md: "0.8rem",
                  },
                }}
              >
                Phone: +91 83708 25457
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: {
                    xs: "0.7rem",
                    sm: "0.75rem",
                    md: "0.8rem",
                  },
                }}
              >
                Address: Decodia P S Abacus - 703, <br />
                7th Floor , NH12, Action Area IIE, Newtown,
                <br />
                Kolkata , West Bengal 700161
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: "#D2691E",
                  fontSize: {
                    xs: "0.8rem",
                    sm: "0.85rem",
                    md: "0.9rem",
                  },
                }}
              >
                Follow Us
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton
                  component="a"
                  href="https://www.facebook.com/decodiadesignstudio"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "#ffffff",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 6px 20px #E4405F40`,
                    },
                  }}
                >
                  <FacebookIcon />
                </IconButton>

                <IconButton
                  component="a"
                  href="https://www.instagram.com/decodiadesignstudio/?igsh=MXVrMmgzMHdxeG1wcg%3D%3D#"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "#ffffff",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 6px 20px #E4405F40`,
                    },
                  }}
                >
                  <InstagramIcon />
                </IconButton>
                <IconButton
                  component="a"
                  href="https://www.linkedin.com/company/decodiadesignsstudio/posts/?feedView=all"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "#ffffff",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 6px 20px #E4405F40`,
                    },
                  }}
                >
                  <LinkedInIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3, bgcolor: "rgba(255,255,255,0.1)" }} />

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: {
                  xs: "0.65rem",
                  sm: "0.7rem",
                  md: "0.75rem",
                },
                textAlign: { xs: "center", sm: "left" },
              }}
            >
              © 2023 Decodia Design Studio. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={submitSuccess}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          Thank you! Your consultation request has been submitted. We'll contact
          you within 24 hours.
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!submitError}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%" }}
        >
          {submitError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InteriorDesignLandingPage;
